import i18next, { exists, t, TOptions } from 'i18next';

import type { ToastType } from '../../react/state/toaster-types';
import { publish, SubscriptionTopic } from '../../viewer/messaging/pubsub';

type NotificationPayload = {
  data?: (string | number)[];
  values?: Record<string, string | number>;
  duration?: number;
  dismissible?: boolean;
  id?: string;
  type?: ToastType;
};

type NotificationResource = {
  type?: string;
  title?: string;
  message?: string;
};

const NOTIFICATIONS_NAMESPACE = 'notifications';

const toValueMap = (data?: (string | number)[]) => {
  if (!data) {
    return {};
  }

  return data.reduce<Record<string, string | number>>((acc, value, index) => {
    acc[index.toString()] = value;
    return acc;
  }, {});
};

const resolveType = (resourceType?: string, override?: ToastType): ToastType => {
  if (override) {
    return override;
  }

  if (resourceType === 'success' || resourceType === 'info' || resourceType === 'error') {
    return resourceType;
  }

  if (resourceType === 'warning' || resourceType === 'warn') {
    return 'warning';
  }

  return 'info';
};

const getLanguageChain = () => {
  if (i18next.languages && i18next.languages.length > 0) {
    return i18next.languages.filter(
      (lng): lng is string => typeof lng === 'string' && lng.length > 0,
    );
  }

  return i18next.language ? [i18next.language] : [];
};

const getNotificationResource = (code: string): NotificationResource => {
  const languages = getLanguageChain();
  const fallbackLanguages = Array.isArray(i18next.options.fallbackLng)
    ? i18next.options.fallbackLng.filter(
        (lng): lng is string => typeof lng === 'string' && lng.length > 0,
      )
    : i18next.options.fallbackLng
      ? [i18next.options.fallbackLng]
      : [];
  const locales = [...languages, ...fallbackLanguages, 'en'].filter(Boolean) as string[];

  for (const lng of locales) {
    const resource = i18next.getResource(lng, NOTIFICATIONS_NAMESPACE, code) as
      | NotificationResource
      | undefined;
    if (resource) {
      return resource;
    }
  }

  return {};
};

export const publishNotification = (code: string, payload: NotificationPayload = {}) => {
  const interpolationValues = {
    ...toValueMap(payload.data),
    ...(payload.values ?? {}),
  };

  const resource = getNotificationResource(code);
  const type = resolveType(resource.type, payload.type);
  const translationOptions: TOptions = {
    ns: NOTIFICATIONS_NAMESPACE,
    ...interpolationValues,
  };

  const defaultTitle = (() => {
    switch (type) {
      case 'success':
        return t('toaster.title.success', { ns: 'common' });
      case 'error':
        return t('toaster.title.error', { ns: 'common' });
      case 'warning':
        return t('toaster.title.warning', { ns: 'common' });
      case 'info':
      default:
        return t('toaster.title.info', { ns: 'common' });
    }
  })();

  const codeTitle = `${code}.title`;
  const codeMessage = `${code}.message`;
  const title = exists(codeTitle, translationOptions)
    ? // eslint-disable-next-line local-rules/valid-react-i18next-t
      t(codeTitle as unknown, translationOptions)
    : defaultTitle;
  const message = exists(codeMessage, translationOptions)
    ? // eslint-disable-next-line local-rules/valid-react-i18next-t
      t(codeMessage as unknown, translationOptions)
    : `Notification ${code} not implemented!`;

  publish(SubscriptionTopic.ToastrMessage, {
    type,
    title,
    message,
    duration: payload.duration,
    dismissible: payload.dismissible,
    id: payload.id,
  });
};
